import { useIM } from '@wui/im'

import { useWebRTC } from '@/entities/webrtc'

import { requestKeyframesOnActiveLocalWebcams } from './request-keyframes-on-active-local-webcams'

const knownCallIdsByRoom = new Map<string, Set<string>>()
let isWatchStarted = false
let joinKeyframeTimer: number | undefined

const scheduleJoinKeyframes = () => {
  if (joinKeyframeTimer !== undefined) {
    window.clearTimeout(joinKeyframeTimer)
  }
  // Small delay: let SFU finish attaching the new subscriber before IDR.
  joinKeyframeTimer = window.setTimeout(() => {
    joinKeyframeTimer = undefined
    void requestKeyframesOnActiveLocalWebcams()
  }, 300)
}

/**
 * When a new call leg appears in a conference we already know, force webcam keyframes
 * so late joiners are not stuck on loader / first undecodable frame.
 */
export const watchConferenceJoinForWebcamKeyframes = (): (() => void) => {
  if (isWatchStarted) {
    return () => undefined
  }
  isWatchStarted = true

  const { sessions } = useWebRTC()
  const { onConferenceUpdated } = useIM()

  onConferenceUpdated((event) => {
    const roomPServed = event.pServed
    const contacts = event.extendedParams?.contacts
    if (!roomPServed || !contacts?.length) return
    if (!sessions.value.size) return

    const nextIds = new Set<string>()
    for (const contact of contacts) {
      for (const call of contact.calls ?? []) {
        if (call.id) nextIds.add(call.id)
      }
    }

    const prevIds = knownCallIdsByRoom.get(roomPServed) ?? new Set<string>()
    const addedIds = [...nextIds].filter((id) => !prevIds.has(id))
    knownCallIdsByRoom.set(roomPServed, nextIds)

    // First snapshot for the room — remember ids; if others already present, keyframe once.
    if (prevIds.size === 0) {
      if (nextIds.size > 1) {
        scheduleJoinKeyframes()
      }
      return
    }

    if (!addedIds.length) return

    scheduleJoinKeyframes()
  })

  return () => undefined
}
